import React, { useState } from "react";
import styled from "styled-components";
import { Col } from "module/common/component/layout";

export interface ExpandableProps {
    defaultExpanded?: boolean;
    expanded?: boolean;
    open?: boolean; // Alias for expanded
    onToggle?: (expanded: boolean) => void;
    onOpen?: () => void; // Called when expanded
    onClose?: () => void; // Called when collapsed
    title?: React.ReactNode;
    children?: React.ReactNode;
    hideIndicator?: boolean;
}

const ExpandableRoot = styled(Col)`
    width: 100%;
`;

const ExpandableHeaderButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    width: 100%;

    &:hover {
        background-color: ${({ theme }) => theme.palette.lightGrey};
    }
`;

const ExpandableContent = styled.div<{ expanded: boolean }>`
    max-height: ${({ expanded }) => (expanded ? "1000px" : "0")};
    overflow: hidden;
    transition: max-height 0.3s ease;
`;

const ExpandableComponent: React.FC<ExpandableProps> = ({
    defaultExpanded = false,
    expanded: controlledExpanded,
    open: controlledOpen,
    onToggle,
    onOpen,
    onClose,
    title,
    children,
    hideIndicator = false,
}) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const expanded = controlledOpen !== undefined ? controlledOpen : (controlledExpanded !== undefined ? controlledExpanded : internalExpanded);

    const handleToggle = () => {
        const newExpanded = !expanded;
        if (controlledExpanded === undefined && controlledOpen === undefined) {
            setInternalExpanded(newExpanded);
        }
        onToggle?.(newExpanded);
        if (newExpanded) {
            onOpen?.();
        } else {
            onClose?.();
        }
    };

    return (
        <ExpandableRoot>
            <ExpandableHeaderButton onClick={handleToggle}>
                {title}
                {!hideIndicator && <span>{expanded ? "−" : "+"}</span>}
            </ExpandableHeaderButton>
            <ExpandableContent expanded={expanded}>{children}</ExpandableContent>
        </ExpandableRoot>
    );
};

// Subcomponents for compound component pattern
const ExpandableDisplay: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => <div style={style}>{children}</div>;
const ExpandableHeader: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => <div style={style}>{children}</div>;
const ExpandableBody: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => <div style={style}>{children}</div>;
const ExpandableContentComponent: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => <div style={style}>{children}</div>;

// Create compound component
export const Expandable = Object.assign(ExpandableComponent, {
    Display: ExpandableDisplay,
    Header: ExpandableHeader,
    Body: ExpandableBody,
    Content: ExpandableContentComponent,
});

export default Expandable;


